<script lang="ts">
	import ToolbarButton from './ToolbarButton.svelte'
	import ToolbarDivider from './ToolbarDivider.svelte'
	import type { ToolbarAction } from '@repo/commons/models'

	interface MarkdownToolbarProps {
		isPreviewMode: boolean
		enableImageUpload: boolean
		toolbarConfig: ToolbarAction[]
		canUndo: boolean
		canRedo: boolean
		onPreviewClick: () => void
		onInsertText: (before: string, after?: string) => void
		onImageSelect: () => void
		onUndo: () => void
		onRedo: () => void
		onLink: () => void
		onNumberedList?: () => void
		onBulletList?: () => void
	}

	let {
		isPreviewMode,
		enableImageUpload,
		toolbarConfig,
		canUndo,
		canRedo,
		onPreviewClick,
		onInsertText,
		onImageSelect,
		onUndo,
		onRedo,
		onLink,
		onNumberedList = () => {},
		onBulletList = () => {}
	}: MarkdownToolbarProps = $props()

	const filteredActions = $derived.by(() => {
		return toolbarConfig.filter((action) => {
			// Filter out image upload if disabled
			if (action.type === 'image' && !enableImageUpload) {
				return false
			}
			return true
		})
	})

	function handleButtonClick(action: ToolbarAction) {
		switch (action.type) {
			case 'preview':
				onPreviewClick()
				break
			case 'button':
				if (action.action === 'undo') {
					onUndo()
				} else if (action.action === 'redo') {
					onRedo()
				} else if (action.action === 'link') {
					onLink()
				} else if (action.action === 'numberedList') {
					onNumberedList()
				} else if (action.action === 'bulletList') {
					onBulletList()
				} else if (action.markdownBefore !== undefined) {
					onInsertText(action.markdownBefore, action.markdownAfter || '')
				}
				break
			case 'image':
				onImageSelect()
				break
		}
	}

	function isButtonDisabled(action: ToolbarAction): boolean {
		// Preview button is never disabled
		if (action.type === 'preview') return false

		// Undo/Redo buttons have their own disabled states
		if (action.action === 'undo') return !canUndo || isPreviewMode
		if (action.action === 'redo') return !canRedo || isPreviewMode

		// Other buttons are disabled in preview mode
		return isPreviewMode
	}
</script>

<div class="toolbar">
	{#each filteredActions as action (action.id)}
		{#if action.type === 'divider'}
			<ToolbarDivider />
		{:else}
			<ToolbarButton
				icon={action.icon}
				text={action.text}
				title={action.title}
				active={action.type === 'preview' && isPreviewMode}
				disabled={isButtonDisabled(action)}
				onclick={() => handleButtonClick(action)}
			/>
		{/if}
	{/each}
</div>

<style>
	.toolbar {
		@apply flex items-center flex-wrap gap-0.5 p-2 border-b border-[#002C4D80]/20 bg-gray-50;
		border-bottom-style: solid;
	}
</style>
